import { ChangeEvent, ReactNode, useContext, useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import {
	FilterField,
	FiltersPanel,
	FiltersToggle,
	SearchInput,
	Select,
	UserSelect
} from 'shared/components'
import { useUserRoles } from 'shared/hooks'
import { StoreSettingsContext } from 'store'
import { useFilters } from '../../hooks/useFilters'
import { useFilterOptions } from '../../hooks/useFilterOptions'

const FILTER_PARAMS = [
	'category',
	'product_fabric_id',
	'product_condition_id',
	'product_gender_id',
	'product_type_id',
	'tag',
	'booking_id',
	'published',
	'printed',
	'confirmed',
	'owner'
]

type FiltersSectionProps = {
	actions?: ReactNode
	handleSearch: (data: ChangeEvent<HTMLInputElement>) => void
	handleFilter: ({ id, value }: { id: string; value: string | null }) => void
}

export const FiltersSection = ({ actions, handleSearch, handleFilter }: FiltersSectionProps) => {
	const { t } = useTranslation()
	const [searchParams, setSearchParams] = useSearchParams()
	const { isUser } = useUserRoles()
	const storeCtx = useContext(StoreSettingsContext)
	const settings = storeCtx?.storeSettings
	const filters = useFilters()
	const { categories, fabrics, conditions, genders, types, tags, bookings } = useFilterOptions()
	const [isOpen, setIsOpen] = useState(FILTER_PARAMS.some((param) => searchParams.has(param)))
	const [resetKey, setResetKey] = useState(0)

	const clearFilters = () => {
		const params = new URLSearchParams(searchParams)
		FILTER_PARAMS.forEach((param) => params.delete(param))
		params.delete('cursor')
		setSearchParams(params)
		setResetKey((key) => key + 1)
	}

	return (
		<div className="border-b border-border p-4">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
				{actions}
				<SearchInput
					variant="filled"
					className="min-w-0 flex-1"
					label={t('Product search')}
					placeholder={t('Search products...')}
					onChange={handleSearch}
				/>
				<FiltersToggle isOpen={isOpen} onClick={() => setIsOpen((open) => !open)} />
			</div>
			{isOpen && (
				<FiltersPanel
					key={resetKey}
					className="xl:grid-cols-6"
					title={t('Filter products')}
					onClear={clearFilters}>
					{settings?.categories_enabled && (
						<FilterField label={t('Category')}>
							<Select
								id="category"
								variant="outline"
								placeholder={t('All')}
								isLoading={categories.isLoading}
								options={categories.options}
								handleInfiniteScroll={categories.fetchNextPage}
								onChange={(data) => handleFilter({ id: 'category', value: data?.value || null })}
							/>
						</FilterField>
					)}
					{settings?.product_fabrics_enabled && (
						<FilterField label={t('Fabric')}>
							<Select
								id="fabricType"
								variant="outline"
								placeholder={t('All')}
								isLoading={fabrics.isLoading}
								options={fabrics.options}
								handleInfiniteScroll={fabrics.fetchNextPage}
								onChange={(data) =>
									handleFilter({ id: 'product_fabric_id', value: data?.value || null })
								}
							/>
						</FilterField>
					)}
					{settings?.product_conditions_enabled && (
						<FilterField label={t('Condition')}>
							<Select
								id="condition"
								variant="outline"
								placeholder={t('All')}
								isLoading={conditions.isLoading}
								options={conditions.options}
								handleInfiniteScroll={conditions.fetchNextPage}
								onChange={(data) =>
									handleFilter({ id: 'product_condition_id', value: data?.value || null })
								}
							/>
						</FilterField>
					)}
					{settings?.product_genders_enabled && (
						<FilterField label={t('Gender')}>
							<Select
								id="gender"
								variant="outline"
								placeholder={t('All')}
								isLoading={genders.isLoading}
								options={genders.options}
								handleInfiniteScroll={genders.fetchNextPage}
								onChange={(data) =>
									handleFilter({ id: 'product_gender_id', value: data?.value || null })
								}
							/>
						</FilterField>
					)}
					{settings?.product_types_enabled && (
						<FilterField label={t('Type')}>
							<Select
								id="type"
								variant="outline"
								placeholder={t('All')}
								isLoading={types.isLoading}
								options={types.options}
								handleInfiniteScroll={types.fetchNextPage}
								onChange={(data) =>
									handleFilter({ id: 'product_type_id', value: data?.value || null })
								}
							/>
						</FilterField>
					)}
					{settings?.tags_enabled && (
						<FilterField label={t('Tag')}>
							<Select
								id="tag"
								variant="outline"
								placeholder={t('All')}
								isLoading={tags.isLoading}
								options={tags.options}
								handleInfiniteScroll={tags.fetchNextPage}
								onChange={(data) => handleFilter({ id: 'tag', value: data?.label || null })}
							/>
						</FilterField>
					)}
					<FilterField label={t('Booking')}>
						<Select
							id="booking"
							variant="outline"
							placeholder={t('All')}
							isLoading={bookings.isLoading}
							options={bookings.options}
							handleInfiniteScroll={bookings.fetchNextPage}
							onChange={(data) => handleFilter({ id: 'booking_id', value: data?.value || null })}
						/>
					</FilterField>
					{filters.map((filter) => (
						<FilterField key={filter.id} label={filter.label}>
							<Select
								id={filter.id}
								variant="outline"
								placeholder={filter.placeholder}
								options={filter.options}
								onChange={(data) => handleFilter({ id: filter.id, value: data?.value || null })}
							/>
						</FilterField>
					))}
					{settings?.product_confirm_enabled && (
						<FilterField label={t('Confirmation')}>
							<Select
								id="confirmed"
								variant="outline"
								placeholder={t('All')}
								options={[
									{ value: '1', label: t('Confirmed') },
									{ value: '0', label: t('Unconfirmed') }
								]}
								onChange={(data) => handleFilter({ id: 'confirmed', value: data?.value || null })}
							/>
						</FilterField>
					)}
					{!isUser && (
						<FilterField label={t('Owner')}>
							<UserSelect
								id="owner"
								variant="outline"
								placeholder={t('All')}
								onChange={(data: any) => handleFilter({ id: 'owner', value: data?.value || null })}
							/>
						</FilterField>
					)}
				</FiltersPanel>
			)}
		</div>
	)
}
